import { Map, fromJS } from 'immutable';
import { normalize } from 'normalizr';

import { receiveAndClearSiteAlarmStates } from '@sophocles/entities/AlarmState';
import { receiveCameras } from '@sophocles/entities/Camera';
import { receiveCameraInterfaceControllers } from '@sophocles/entities/CameraInterfaceController';
import { receiveCameraSchedules } from '@sophocles/entities/CameraSchedule';
import { mapListToRecords, normalizeList, websocketsHandlerFactory } from '@sophocles/entities/entityUtils';
import { receiveIntrusionSensorConfigurations } from '@sophocles/entities/IntrusionSensorConfiguration';
import { pendingUnitCommandSchema, receivePendingUnitCommands } from '@sophocles/entities/PendingUnitCommand';
import { receiveRadios } from '@sophocles/entities/Radio';
import { receiveRelays } from '@sophocles/entities/Relay';
import { receiveRelayControllerFirmwareVersions } from '@sophocles/entities/RelayControllerFirmwareVersion';
import { receiveRelayLoads } from '@sophocles/entities/RelayLoad';
import { receiveRelaySchedules } from '@sophocles/entities/RelaySchedule';
import { Site, siteSchema } from '@sophocles/entities/Site/siteRecord';
import { receiveSiteConfigurations } from '@sophocles/entities/SiteConfiguration';
import { receiveSiteMaintenanceLogs } from '@sophocles/entities/SiteMaintenanceLog';
import { receiveSiteWeatherConfigurations } from '@sophocles/entities/SiteWeatherConfiguration';
import { receiveSmartlinkUnits, smartlinkUnitSchema } from '@sophocles/entities/SmartlinkUnit';
import { receiveLimitedUsers } from '@sophocles/entities/User';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction, mapToQueryParams } from '@sophocles/utils';

/* RECEIVE ACTIONS */
export const RECEIVE_SITES = 'RECEIVE_SITES';
export function receiveSites(json) {
  return {
    type: RECEIVE_SITES,
    sites: mapListToRecords(json, Site),
  };
}

export const RECEIVE_SITE = 'RECEIVE_SITE';
export function receiveSite(json) {
  return {
    type: RECEIVE_SITE,
    sites: Site(json),
  };
}

export const SEED_SITE_CONTEXT = 'SEED_SITE_CONTEXT';
/**
 * Merge navigation-context fields (name, coordinates, site_groups, …) into
 * the stored site record without disturbing fields only the full
 * /sites/{id}/ fetch provides. RECEIVE_SITES wraps its payload in complete
 * Site records, so seeding through it replaces an already-fetched record and
 * nulls relay_controller — the fetch key the readings pages gate on.
 *
 * @param {string|number} siteId - site id; the store is keyed by string id.
 * @param {Object} fields        - Site record fields to set verbatim.
 */
export function seedSiteContext(siteId, fields) {
  return {
    type: SEED_SITE_CONTEXT,
    siteId: String(siteId),
    fields,
  };
}

/* REQUEST ACTIONS */
export const REQUEST_SITE_GROUP_SITES = 'REQUEST_SITE_GROUP_SITES';
function requestSiteGroupSites(sitegroup_id) {
  return {
    type: REQUEST_SITE_GROUP_SITES,
    sitegroup_id,
  };
}

export const REQUEST_SITE = 'REQUEST_SITE';
function requestSite(site_id) {
  return {
    type: REQUEST_SITE,
    site_id,
  };
}

/* REMOVE ACTION */
export const REMOVE_SITES = 'REMOVE_SITES';
export function removeSites() {
  return {
    type: REMOVE_SITES,
  };
}

/* FETCH ACTIONS */
export function fetchSitesForSiteGroup(siteGroup, extraParams = {}) {
  return dispatch => {
    dispatch(requestSiteGroupSites(siteGroup));
    return apiClient
      .get(`/sitegroups/${siteGroup}/sites/`, { params: { limit: 1000, ...extraParams } })
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data.results, 'sites', siteSchema))
      .then(json => {
        dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits));
        dispatch(receiveRelayControllerFirmwareVersions(json.entities.relayControllerFirmwareVersions));
        dispatch(receiveRadios(json.entities.radios));
        dispatch(receiveSiteConfigurations(json.entities.siteConfigurations));
        // dispatch(receiveIntrusionSensorConfigurations(json.entities.intrusionSensorConfigurations)); doesn't exist in the response yet.
        dispatch(receiveCameraInterfaceControllers(json.entities.cameraInterfaceControllers));
        return dispatch(receiveSites(json.entities.sites));
      });
  };
}

export function fetchSite(site_id) {
  return dispatch => {
    dispatch(requestSite(site_id));
    return apiClient
      .get(`/sites/${site_id}/`)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, siteSchema))
      .then(json => {
        dispatch(receiveRelays(json.entities.relays));
        dispatch(receiveRelaySchedules(json.entities.relaySchedules));
        dispatch(receiveRelayLoads(json.entities.relayLoads));
        dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits));
        dispatch(receiveRadios(json.entities.radios));
        dispatch(receiveSiteConfigurations(json.entities.siteConfigurations));
        dispatch(receiveAndClearSiteAlarmStates(site_id, json.entities.alarmStates));
        dispatch(receiveSiteMaintenanceLogs(json.entities.siteMaintenanceLogs));
        dispatch(receiveLimitedUsers(json.entities.users));
        dispatch(receiveIntrusionSensorConfigurations(json.entities.intrusionSensorConfigurations));
        dispatch(receiveCameraInterfaceControllers(json.entities.cameraInterfaceControllers));
        dispatch(receiveCameras(json.entities.cameras));
        dispatch(receiveCameraSchedules(json.entities.cameraSchedules));
        dispatch(receiveSiteWeatherConfigurations(json.entities.siteWeatherConfigurations));
        dispatch(receiveRelayControllerFirmwareVersions(json.entities.relayControllerFirmwareVersions));
        return dispatch(receiveSites(json.entities.sites));
      })
      .catch(error => dispatch(genericErrorAction(error, REQUEST_SITE)));
  };
}

export function fetchRawSite(site_id) {
  return dispatch =>
    apiClient
      .get(`/sites/${site_id}/`)
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_SITE)));
}

/* Edit Actions */
export const EDIT_SITE = 'EDIT_SITE';
export function editSite(site_id) {
  return {
    type: EDIT_SITE,
    site: site_id,
  };
}

export function patchSite(site_id, values) {
  return dispatch => {
    dispatch(editSite(site_id));
    return apiClient
      .patch(`/sites/${site_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, siteSchema))
      .then(json => {
        dispatch(receiveSites(json.entities.sites));
      });
  };
}

/* Create Actions */
export const CREATE_SITE = 'CREATE_SITE';
function sayCreateSite(values) {
  return {
    type: CREATE_SITE,
    values: values,
  };
}

// DEPRECATED(grid-legacy-cleanup): legacy redux create thunk. Only live caller
// is AssignOrCreateDialog (inventory); remove once it migrates to
// useCreateSiteMutation.
export function createSite(values) {
  return dispatch => {
    dispatch(sayCreateSite(values));
    return apiClient
      .post(`/sites/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, siteSchema))
      .then(json => dispatch(receiveSites(json.entities.sites)))
      .catch(error => dispatch(genericErrorAction(error, CREATE_SITE)));
  };
}

/* Delete Actions */
export const DELETE_SITE = 'DELETE_SITE';
function sayDeleteSite(site_id) {
  return {
    type: DELETE_SITE,
    site: site_id,
  };
}

export const DEACTIVATE_SITE = 'DEACTIVATE_SITE';

export function deactivateSite(site, radioAction) {
  return dispatch => {
    dispatch({ type: DEACTIVATE_SITE, site });
    return apiClient
      .post(`/sites/${site}/deactivate/`, {
        radio_action: radioAction,
      })
      .then(checkHttpStatus)
      .then(response => normalize(response.data, siteSchema))
      .then(json => dispatch(receiveSites(json.entities.sites)))
      .catch(error => dispatch(genericErrorAction(error, DEACTIVATE_SITE)));
  };
}

export const ACTIVATE_SITE = 'ACTIVATE_SITE';

export function activateSite(site) {
  return dispatch => {
    dispatch({ type: DEACTIVATE_SITE, site });
    return apiClient
      .post(`/sites/${site}/activate/`, {})
      .then(checkHttpStatus)
      .then(response => normalize(response.data, siteSchema))
      .then(json => dispatch(receiveSites(json.entities.sites)))
      .catch(error => dispatch(genericErrorAction(error, DEACTIVATE_SITE)));
  };
}

export const CLONE_SITE_CONFIGURATION = 'CLONE_SITE_CONFIGURATION';

export function cloneSiteConfiguration(source, destinations, options) {
  return dispatch => {
    dispatch({
      type: CLONE_SITE_CONFIGURATION,
      source,
      destinations,
      options,
    });

    const data = {
      destination_sites: destinations,
      options,
    };

    return apiClient
      .post(`/sites/${source}/clone/`, data)
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, CLONE_SITE_CONFIGURATION)));
  };
}

/* Sending a unit command */
export const SENDING_UNIT_COMMAND = 'SENDING_UNIT_COMMAND';
export function sendUnitCommand(site_id, values) {
  return dispatch => {
    dispatch({ type: SENDING_UNIT_COMMAND, site: site_id, data: values });
    return apiClient
      .post(`/sites/${site_id}/send_command/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, pendingUnitCommandSchema))
      .then(json => dispatch(receivePendingUnitCommands(json.entities.pendingUnitCommands)))
      .catch(error => dispatch(genericErrorAction(error, SENDING_UNIT_COMMAND)));
  };
}

export const REQUEST_SITE_ANCESTRY = 'REQUEST_SITE_ANCESTRY';
export function fetchSiteAncestry(site) {
  return dispatch => {
    dispatch({ type: REQUEST_SITE_ANCESTRY, site });
    return apiClient
      .get(`/sites/${site}/primary_site_group/`)
      .then(checkHttpStatus)
      .then(response => response.data)
      .catch(error => dispatch(genericErrorAction(error, REQUEST_SITE_ANCESTRY)));
  };
}

export const REQUEST_SITE_HARDWARE_HISTORY = 'REQUEST_SITE_HARDWARE_HISTORY';
export function fetchSiteHardwareHistory(site) {
  return dispatch => {
    dispatch({ type: REQUEST_SITE_HARDWARE_HISTORY, site });
    return apiClient
      .get(`/sites/${site}/hardwarehistory/`)
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_SITE_HARDWARE_HISTORY)));
  };
}

export function postRelayActions(siteId, payload) {
  return dispatch =>
    apiClient
      .post(`/sites/${siteId}/relay_actions/`, payload)
      .then(checkHttpStatus)
      .then(response => {
        const json = normalize(response.data, smartlinkUnitSchema);
        dispatch(receiveRelays(json.entities.relays));
        dispatch(receiveRelayLoads(json.entities.relayLoads));
        dispatch(receiveRelaySchedules(json.entities.relaySchedules));
        dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits));
      })
      .catch(error => dispatch(genericErrorAction(error, 'POST_RELAY_ACTIONS')));
}

/** change device * */
export const CHANGE_DEVICE = 'CHANGE_DEVICE';
export function changeSiteDevice(site, data) {
  return dispatch => {
    dispatch({
      type: CHANGE_DEVICE,
      site,
      data,
    });
    return apiClient
      .post(`/sites/${site}/changedevice/`, data)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, siteSchema))
      .then(json => {
        dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits));
        dispatch(receiveRelays(json.entities.relays));
        dispatch(receiveRadios(json.entities.radios));
        dispatch(receiveSiteConfigurations(json.entities.siteConfigurations));
        dispatch(receiveCameraInterfaceControllers(json.entities.cameraInterfaceControllers));
        dispatch(receiveCameras(json.entities.cameras));
        dispatch(receiveCameraSchedules(json.entities.cameraSchedules));
        return dispatch(receiveSites(json.entities.sites));
      })
      .catch(error => dispatch(genericErrorAction(error, CHANGE_DEVICE)));
  };
}

export function fetchSiteAlarmCountsWithFilters(filters) {
  return dispatch =>
    apiClient
      .get(`/sites/alarmcounts/?${mapToQueryParams(Map(filters))}`)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, 'REQUEST_ALARM_COUNTS')));
}

export function fetchSitesCarrierRadioCountsWithFilters(filters) {
  return dispatch =>
    apiClient
      .get(`/sites/carrierunitcount/?${mapToQueryParams(Map(filters))}`)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, 'REQUEST_CARRIER_UNIT_COUNTS')));
}

const receiver = (dispatch, json) => {
  dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits));
  dispatch(receiveRelays(json.entities.relays));
  dispatch(receiveRadios(json.entities.radios));
  dispatch(receiveSiteConfigurations(json.entities.siteConfigurations));
  dispatch(receiveSiteMaintenanceLogs(json.entities.siteMaintenanceLogs));
  dispatch(receiveLimitedUsers(json.entities.users));
  dispatch(receiveIntrusionSensorConfigurations(json.entities.intrusionSensorConfigurations));
  dispatch(receiveCameraInterfaceControllers(json.entities.cameraInterfaceControllers));
  dispatch(receiveCameras(json.entities.cameras));
  dispatch(receiveCameraSchedules(json.entities.cameraSchedules));
  return dispatch(receiveSites(json.entities.sites));
};

export const handleSiteSocketActions = websocketsHandlerFactory(receiver, sayDeleteSite, siteSchema);
